import { Injectable } from '@angular/core';
import { OrigamiHttpResponse, OrigamiResponse, ServiceBase } from './base/service.base';
import { Observable } from 'rxjs';
import { TopicField } from '@app/origami-api/utils/topic-utils';
import { ErrorCode } from '@app/origami-api/constants';
import { ValidationData } from '@app/session/user-details';
import { AdditionalRequestOptions } from '@app/origami-api/services/base/request-proxy/request-proxy.class';

export enum ESignatureType {
  Serial = 1,
  Parallel = 2,
}

export enum ESignatureOriginType {
  General = 0,
  Workflow = 1,
}

export namespace DocuSign {
  export namespace SubmissionView {
    export enum SignEvent {
      send = 'send', // (user sends the envelope)
      save = 'save', // (user saves the envelope)
      cancel = 'cancel', // (user cancels the sending transaction. No envelopeId is returned in this case.)
      error = 'error', // (there is an error when performing the send)
      sessionEnd = 'sessionend', // (the sending session ends before the user completes another action)
    }
  }

  export namespace SigningView {
    export enum SignEvent {
      cancel = 'cancel', // (recipient cancels signing)
      decline = 'decline', // (recipient declines signing)
      exception = 'exception', // (exception occurs)
      faxPending = 'fax_pending', // (recipient has fax pending)
      idCheckFault = 'id_check_faild', // (recipient failed an ID check), NO TYPO THERE (regarding "faild")
      sessionTimeout = 'session_timeout', // (session times out)
      signingComplete = 'signing_complete', // (recipient completes signing)
      ttlExpired = 'ttl_expired', // (the TTL expires)
      viewingComplete = 'viewing_complete', // (recipient completes viewing the envelope)
    }
  }
}

// ???
export interface OrigamiResponseWithAuthUrl<T> {
  AuthValidationUrl?: string;
  Success?: boolean;
  ErrorCode: number;
  Result: T;
  ErrorMessage?: string;
}

export namespace ESignatureServiceOptions {
  /* create e-signature request */
  // import SignEvent = DocuSign.SubmissionView.SignEvent;

  export interface SignerData {
    SignerName: string;
    Title?: string;
    SignerRole?: string;
  }
  export interface CreateESignatureRequestData {
    Signatures: SignerData[];
    Type: ESignatureType;
    OriginType: ESignatureOriginType;
  }
  export interface CreateESignatureRequestParams {
    siteId: number;
    documentIds: number[];
    request: CreateESignatureRequestData;
  }

  /* sign document */
  export interface ESignature {
    Id: number;
    Order: number;
    SignerName: string;
    SignerEmailAddress: string;
    SignerDisplayName: string;
    Title: string;
    Comments: string;
    Reason: string;
    DateTime: string;
    CanStartSigning: boolean;
    Status: number;
    TopicFields: TopicField[];
    User: any;
  }
  // sign
  export interface SignDocumentParams {
    siteId: number;
    documentId: number;
    signature: {
      Comments?: string;
      Reason?: string;
      TopicFields: TopicField[];
    };
  }

  export interface SelfSignDocumentParams {
    siteId: number;
    documentId: number;
    reason?: string;
    comment?: string;
    validationData?: ValidationData; //optional?
  }
  // base, not used?
  export interface SignDocumentsParams {
    siteId: number;
    documentsList: any[]; // Topic[] ?
    reason: string;
  }

  export interface UpdateESignatureRequestState {
    siteId: number;
    envelopeId: string;
    signEvent?: DocuSign.SubmissionView.SignEvent | DocuSign.SigningView.SignEvent;
  }

  export interface GetAuthorizationUrlParams {
    siteId: number;
  }

  export interface GetAdobeSignAgreementStatusParams {
    siteId: number;
    externalId?: string;
  }

  export interface CancelESignatureRequestParams {
    siteId: number;
    documentIds: number[];
  }

  export interface CheckDocumentSupportByESignVendor {
    siteId: number;
    documentId: number;
  }

  export interface GetAdobeSignSkipInternalValidation {
    siteId: number;
  }
}

export enum ResumeOutcome {
  notApplicable = 0,
  resumable = 1,
  alreadySent = 2,
  notResumable = 3,
}

export namespace ESignatureServiceModel {
  export interface ESignatureResult {
    ErrorRecords: number[];
    ExternalId?: string;
    SendingViewUrl: string;
  }

  export interface ResumeResult {
    Outcome: ResumeOutcome;
    SendingViewUrl: string;
    Status: number;
    ErrorRecords: number[];
  }
  export interface ESignatureSingResult {
    ErrorRecords: number[];
    ExternalId?: string;
    SigningViewUrl: string;
  }
}

@Injectable()
export class ESignatureService extends ServiceBase {
  serviceUrl = 'ESignatureService.svc';

  cancelESignatureRequest(
    body: ESignatureServiceOptions.CancelESignatureRequestParams,
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/CancelESignatureRequest', body, null, null, true);
  }

  createESignatureRequest(
    body: ESignatureServiceOptions.CreateESignatureRequestParams,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<ESignatureServiceModel.ESignatureResult>> {
    return this.origamiPost('/CreateESignatureRequest', body, null, silentErrorCodes, true);
  }

  declineDocument(
    body: ESignatureServiceOptions.SignDocumentParams,
    silentErrorCodes?: ErrorCode[],
    additionalRequestOptions?: AdditionalRequestOptions,
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/DeclineDocument', body, null, silentErrorCodes, true, additionalRequestOptions);
  }

  getAdobeSignAgreementStatus(
    body: ESignatureServiceOptions.GetAdobeSignAgreementStatusParams,
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/GetAdobeSignAgreementStatus', body, null, null, true);
  }

  // Live-polls Adobe for the agreement status (throttled server-side via the distributed cache).
  // force=true bypasses the throttle for the just-sent moment.
  refreshAdobeSignAgreementStatus(body: {
    siteId: number;
    externalId: string;
    force?: boolean;
  }): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/RefreshAdobeSignAgreementStatus', body, null, null, true);
  }

  // Reopens an Adobe draft the sender abandoned mid-compose; the server re-checks the agreement and
  // either returns an authoring URL or advances a request that turned out to have been sent (TRL-22472).
  resumeAdobeSignAgreement(body: {
    siteId: number;
    externalId: string;
  }): Observable<OrigamiResponse<ESignatureServiceModel.ResumeResult>> {
    return this.origamiPost('/ResumeAdobeSignAgreement', body, null, null, true);
  }

  getAuthorizationUrl(body: ESignatureServiceOptions.GetAuthorizationUrlParams): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/GetAuthorizationUrl', body, null, null, true);
  }

  signDocument(
    body: ESignatureServiceOptions.SignDocumentParams,
    silentErrorCodes?: ErrorCode[],
    additionalRequestOptions?: AdditionalRequestOptions,
  ): Observable<OrigamiResponseWithAuthUrl<ESignatureServiceModel.ESignatureSingResult>> {
    return this.origamiPost('/SignDocument', body, null, silentErrorCodes, true, additionalRequestOptions);
  }

  selfSignDocument(
    body: ESignatureServiceOptions.SelfSignDocumentParams,
    silentErrorCodes?: ErrorCode[],
    additionalRequestOptions?: AdditionalRequestOptions,
  ): Observable<OrigamiResponseWithAuthUrl<ESignatureServiceModel.ESignatureSingResult>> {
    return this.origamiPost('/SelfSignDocument', body, null, silentErrorCodes, true, additionalRequestOptions);
  }

  updateESignatureRequestState(
    body: ESignatureServiceOptions.UpdateESignatureRequestState,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/UpdateESignatureRequestState', body, null, silentErrorCodes, true);
  }

  checkDocumentSupportByESignVendor(
    body: ESignatureServiceOptions.CheckDocumentSupportByESignVendor,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<boolean>> {
    return this.origamiPost('/CheckDocumentSupportByESignVendor', body, null, silentErrorCodes, true);
  }

  getAdobeSignSkipInternalValidation(
    body: ESignatureServiceOptions.GetAdobeSignSkipInternalValidation,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<boolean>> {
    return this.origamiPost('/GetAdobeSignSkipInternalValidation', body, null, silentErrorCodes, true);
  }
}
